import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { Accordion, Modal } from "react-bootstrap";
import Select from "react-select";
import defaultAvatar from "../../../../src/assets/images/placeholder.jpg";
import icon from "../../../../src/assets/images/share-link.png";
import Reshedule from "../../../../src/assets/images/deadline.png";
import completeIcon from "../../../../src/assets/images/check_11601272.png";
import { getBranchesAction } from "../../../store/actions/BranchAction";
import { getDoctorsAction } from "../../../store/actions/DoctorsAction";
import {
  getAssignedAppointmentAction,
  getAvailableSlotsAction,
  confirmAppointmentAction,
  getConfirmedAction,
  removeAppointmentAction,
} from "../../../store/actions/AssignedAppointmentAction";
import CommonPaymentModal from "../Common/CommonPaymentModal";
import { collectPaymentAction } from "../../../store/actions/ReceptionistAction";
import collectPaymentIcon from "../../../../src/assets/images/collect_payment.png";
import { sendFormLinkAction, rescheduleAppointmentAction } from "../../../store/actions/ReceptionistAction";
import { formatDate } from "../../../utils/dateUtils";
import { collectAppointmentPayment } from "../../../services/AppointmentService";

// Utility function to get today's date in YYYY-MM-DD format
const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AssignedAppointment = () => {
  const dispatch = useDispatch();
  const isMounted = useRef(true);

  // ==================== STATE DECLARATIONS ====================
  // Filters
  const [selectedDate, setSelectedDate] = useState(getTodayDateString());
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [selectedBranch, setSelectedBranch] = useState(null);

  // Time slots
  const [selectedTimeSlots, setSelectedTimeSlots] = useState({});
  const [timeSlots, setTimeSlots] = useState([]);
  const [slotLoading, setSlotLoading] = useState(false); // ✅ New loading state

  // Modals
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // Reschedule form
  const [modalDate, setModalDate] = useState(getTodayDateString());
  const [modalBranch, setModalBranch] = useState(null);
  const [modalMode, setModalMode] = useState("Online");
  const [modalTimeSlot, setModalTimeSlot] = useState(null);

  // Payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPatientForPayment, setSelectedPatientForPayment] = useState(null);
  const [collectingPayment, setCollectingPayment] = useState(false);

  // Branch edit
  const [editBranch, setEditBranch] = useState(null);
  const [editAppointmentId, setEditAppointmentId] = useState(null);
  const [editAppointmentType, setEditAppointmentType] = useState(null);

  // Loading states
  const [branchUpdateLoading, setBranchUpdateLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState({ assigned: false, confirmed: false });
  const [confirmLoading, setConfirmLoading] = useState({});
  const [removeLoading, setRemoveLoading] = useState({});
  const [sendLinkLoading, setSendLinkLoading] = useState({});
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  // Local data
  const [localAssignedAppointments, setLocalAssignedAppointments] = useState([]);
  const [localConfirmedAppointments, setLocalConfirmedAppointments] = useState([]);

  // ==================== REDUX SELECTORS ====================
  const doctors = useSelector((state) => state.doctors.doctors || []);
  const branches = useSelector((state) => state.branches?.branches || []);
  const loading = useSelector((state) => state.doctors.loading || false);
  const { assigned = [], confirmed = [], availableSlots = [], summary = {} } = useSelector(
    (state) => state.assignedAppointments || {}
  );

  // ==================== HELPER FUNCTIONS ====================
  // Check if patient is a Lead (patient_id invalid)
  const isLeadPatient = (patient) => {
    return !patient?.patient_id || patient?.patient_id === "0" || patient?.patient_id === 0;
  };

  // Show Collect Payment button when payment is pending (is_payment === "No")
  const shouldShowCollectPayment = (patient) => {
    return patient?.is_payment === "No";
  };

  // ==================== LIFECYCLE & INITIALIZATION ====================
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    dispatch(getBranchesAction(false));
    dispatch(getDoctorsAction(false));
  }, [dispatch]);

  // Set default doctor (first from list)
  useEffect(() => {
    if (doctors.length > 0 && !selectedDoctorId) {
      setSelectedDoctorId(doctors[0].doctor_id);
    }
  }, [doctors, selectedDoctorId]);

  // ✅ FIX 1: Reset slots and set loading when doctor or date changes
  useEffect(() => {
    setTimeSlots([]);
    setSelectedTimeSlots({});
    setSlotLoading(true);
  }, [selectedDoctorId, selectedDate]);

  // ==================== API CALLS ====================
  const fetchAssignedAppointments = useCallback(async () => {
    if (!isMounted.current) return;
    setIsRefreshing((prev) => ({ ...prev, assigned: true }));
    const payload = {
      pagination:"No" ,
      pageId: 1,
      status: "Waiting",
      appointment_visited: "No",
      is_payment: "Yes",
      is_confirmed: "No",
      branch: selectedBranch?.value || "",
      mode: "",
      doctor_id: "0",
      date: selectedDate || getTodayDateString(),
      search_keyword: "",
      sort_by: "date",
      sort_order: "ASC",
    };
    try {
      await dispatch(getAssignedAppointmentAction(payload));
    } finally {
      if (isMounted.current) setIsRefreshing((prev) => ({ ...prev, assigned: false }));
    }
  }, [dispatch, selectedBranch, selectedDate]);

  const fetchConfirmedAppointments = useCallback(async () => {
    if (!isMounted.current || !selectedDoctorId) return;
    setIsRefreshing((prev) => ({ ...prev, confirmed: true }));
    const payload = {
      doctor_id: selectedDoctorId,
      pagination: true,
      pageId: 1,
      branch_id: selectedBranch?.value || "",
      mode: "",
      date: selectedDate || getTodayDateString(),
      status: "",
      search_keyword: "",
      sort_by: "",
      sort_order: "",
    };
    try {
      await dispatch(getConfirmedAction(payload));
    } finally {
      if (isMounted.current) setIsRefreshing((prev) => ({ ...prev, confirmed: false }));
    }
  }, [dispatch, selectedDoctorId, selectedBranch, selectedDate]);

  const refreshBothLists = useCallback(async () => {
    await Promise.all([fetchAssignedAppointments(), fetchConfirmedAppointments()]);
  }, [fetchAssignedAppointments, fetchConfirmedAppointments]);

  useEffect(() => {
    fetchAssignedAppointments();
    fetchConfirmedAppointments();
  }, [fetchAssignedAppointments, fetchConfirmedAppointments]);

  // ✅ FIX 2: Fetch fresh slots when doctor/date change (with loading state)
  useEffect(() => {
    const fetchSlots = async () => {
      if (!selectedDoctorId || !selectedDate) {
        setSlotLoading(false);
        return;
      }
      setSlotLoading(true);
      try {
        await dispatch(getAvailableSlotsAction({ doctor_id: selectedDoctorId, date: selectedDate }));
      } catch (err) {
        console.error("Error fetching slots:", err);
        setSlotLoading(false);
      }
    };
    fetchSlots();
  }, [selectedDoctorId, selectedDate, dispatch]);

  // Process available slots from Redux
  useEffect(() => {
    if (!availableSlots.length) {
      setTimeSlots([]);
      setSlotLoading(false);
      return;
    }
    const mappedSlots = availableSlots.map((slot) => ({
      value: slot.time_slot,
      label: slot.time_slot,
      opening_hour_id: slot.opening_hour_id,
      available_slots: slot.available_slots,
      max_appointments: slot.max_appointments,
    }));
    setTimeSlots(mappedSlots);
    setSlotLoading(false);
  }, [availableSlots]);

  // ==================== DATA TRANSFORMATION (LOCAL STATE) ====================
  useEffect(() => {
    const transformedAssigned = assigned
      .filter((item) => item?.appointment_id)
      .map((item) => ({
        id: item.appointment_id,
        appointment_id: item.appointment_id,
        name: item.patient_full_name || item.patient_name || "",
        patient_id: item.patient_id,
        lead_id: item.lead_id,
        doctor: item.doctor_name || "",
        disease: item.diseases
          ? [...new Set(item.diseases.split(",").map((d) => d.trim()).filter(Boolean))].join(", ")
          : "",
        timeslot: item.time_slot || "",
        date: item.date || "",
        is_payment: item.is_payment,
        branch: item.branch_name || "",
        branch_id: item.branch_id || "",
        avatar: item.avatar || null,
        phone_no: item.phone_no || "",
        email: item.email || "",
        mode: item.mode || "",
        status: item.appointment_status || "",
        appointment_camp_reg: item.appointment_camp_reg || "",
        is_firsttime_visitor: item.is_firsttime_visitor,
        queue_id: item.queueDetails?.queue_id,
      }));
    setLocalAssignedAppointments(transformedAssigned);
  }, [assigned]);

  useEffect(() => {
    const transformedConfirmed = confirmed.map((timeSlotData) => {
      const [open_time, close_time] = (timeSlotData.time_slot || "").split("-");
      return {
        time: timeSlotData.time_slot,
        open_time,
        close_time,
        label: `${open_time || "N/A"} - ${close_time || "N/A"}`,
        opening_hour_id: timeSlotData.opening_hour_id,
        doctor_name: timeSlotData.doctor_name,
        confirmed_count: timeSlotData.confirmed_count,
        allowed_count: timeSlotData.allowed_count,
        available_slots: timeSlotData.available_slots,
        date: timeSlotData.date,
        patients: (timeSlotData.patients || []).map((patient) => {
          const branchObj = branches.find((b) => String(b.branch_id) === String(patient.branch_id));
          const isPresented = patient.queueDetails?.is_presented === "Yes";
          return {
            id: patient.appointment_id,
            appointment_id: patient.appointment_id,
            name: patient.patient_full_name || patient.patient_name,
            disease: patient.diseases || "-",
            doctor: timeSlotData.doctor_name,
            patient_id: patient.patient_id,
            lead_id: patient.lead_id,
            timeslot: timeSlotData.time_slot,
            date: timeSlotData.date,
            branch: branchObj?.branch_name || "-",
            branch_id: patient.branch_id || "",
            avatar: patient.avatar || null,
            phone_no: patient.phone_no,
            email: patient.email,
            appointment_status: patient.appointment_status,
            mode: patient.mode,
            is_presented: isPresented,
            queueDetails: patient.queueDetails,
            is_payment: patient.is_payment,
          };
        }),
      };
    });
    setLocalConfirmedAppointments(transformedConfirmed);
  }, [confirmed, branches]);

  // ==================== SELECT OPTIONS & STYLES ====================
  const doctorOptions = doctors.map((doctor) => ({
    value: doctor.doctor_id,
    label: doctor.doctor_name,
    specialization: doctor.specialization,
  }));
  const selectedDoctorOption = doctorOptions.find((opt) => String(opt.value) === String(selectedDoctorId));

  const formatOptionLabel = ({ label, specialization }) => (
    <div>
      <div style={{ fontWeight: "bold" }}>{label}</div>
      <div style={{ fontSize: "0.8em", color: "#666" }}>{specialization}</div>
    </div>
  );

  const customStyles = {
    control: (base) => ({ ...base, minHeight: "38px", fontSize: "14px" }),
    valueContainer: (base) => ({ ...base, padding: "0 8px" }),
    input: (base) => ({ ...base, margin: 0, fontSize: "14px" }),
    placeholder: (base) => ({ ...base, fontSize: "14px" }),
    singleValue: (base) => ({ ...base, fontSize: "14px" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  // ==================== EVENT HANDLERS ====================
  const handleDoctorChange = (selectedOption) => setSelectedDoctorId(selectedOption ? selectedOption.value : "");
  const handleDateChange = (e) => setSelectedDate(e.target.value);
  const handleBranchFilterChange = (selectedOption) => setSelectedBranch(selectedOption);
  const handleTimeSlotChange = (selectedOption, patientId) =>
    setSelectedTimeSlots((prev) => ({ ...prev, [patientId]: selectedOption }));

  const handleSendLink = async (patientId) => {
    setSendLinkLoading((prev) => ({ ...prev, [patientId]: true }));
    try {
      await dispatch(sendFormLinkAction(patientId));
    } finally {
      setSendLinkLoading((prev) => ({ ...prev, [patientId]: false }));
    }
  };

  const handleConfirmDoctorAssign = async (appointmentId, patientName, patientBranchId, isBranchEdit = false) => {
    const selectedSlot = selectedTimeSlots[appointmentId];
    if (!selectedSlot) {
      Swal.fire("Validation Error", "Please select a time slot first", "error");
      return;
    }
    if (!selectedDoctorId) {
      Swal.fire("Validation Error", "Please select a doctor first", "error");
      return;
    }
    const result = await Swal.fire({
      title: isBranchEdit ? "Confirm Branch Update" : "Confirm Doctor Assignment",
      text: isBranchEdit ? `Update branch for ${patientName}?` : `Assign this slot to ${patientName}?`,
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      setConfirmLoading((prev) => ({ ...prev, [appointmentId]: true }));
      try {
        const confirmPayload = {
          appointment_id: appointmentId,
          doctor_id: selectedDoctorId,
          time_slot: selectedSlot.value,
          doctor_opening_hour_id: selectedSlot.opening_hour_id,
          branch_id: isBranchEdit ? editBranch?.value : patientBranchId,
        };
        await dispatch(confirmAppointmentAction(confirmPayload));
        setSelectedTimeSlots((prev) => {
          const newState = { ...prev };
          delete newState[appointmentId];
          return newState;
        });
        if (isBranchEdit) {
          setShowBranchModal(false);
          setEditBranch(null);
          setEditAppointmentId(null);
          setBranchUpdateLoading(false);
        }
        await refreshBothLists();
        // Swal.fire(
        //   "Success!",
        //   isBranchEdit
        //     ? "Branch has been updated."
        //     : "Branch has been added.",
        //   "success"
        // );
      } finally {
        setConfirmLoading((prev) => ({ ...prev, [appointmentId]: false }));
        if (isBranchEdit) setBranchUpdateLoading(false);
      }
    } else {
      if (isBranchEdit) setBranchUpdateLoading(false);
    }
  };

  const handleRescheduleWithApi = async () => {
    if (!modalDate || !modalBranch) {
      Swal.fire("Validation Error", "Please fill all reschedule fields (Date, Branch)", "error");
      return;
    }
    const payload = {
      appointment_id: Number(selectedAppointment?.appointment_id),
      date: modalDate,
      mode: modalMode,
      branch: modalBranch?.value || "",
    };
    setRescheduleLoading(true);
    try {
      const res = await dispatch(rescheduleAppointmentAction(payload));
      if (res?.status === "success") {
        Swal.fire("Success", res.message, "success");
        setShowInfoModal(false);
        await refreshBothLists();
      } else {
        throw new Error(res?.message || "Failed to reschedule");
      }
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to reschedule", "error");
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleOpenBranchModal = (patient, type) => {
    setSelectedAppointment(patient);
    setEditAppointmentId(patient.id);
    setEditAppointmentType(type);
    const branchOption = branches.find((b) => String(b.branch_name) === String(patient.branch));
    setEditBranch(branchOption ? { value: branchOption.branch_id, label: branchOption.branch_name } : null);
    setShowBranchModal(true);
  };

  const handleBranchModalConfirm = () => {
    if (!editBranch) {
      Swal.fire("Warning", "Please select a branch", "warning");
      return;
    }
    if (editAppointmentType === "assigned") {
      setLocalAssignedAppointments((prev) =>
        prev.map((apt) =>
          apt.id === editAppointmentId ? { ...apt, branch: editBranch.label, branch_id: editBranch.value } : apt
        )
      );
    } else if (editAppointmentType === "confirmed") {
      setLocalConfirmedAppointments((prev) =>
        prev.map((slot) => ({
          ...slot,
          patients: slot.patients.map((p) =>
            p.id === editAppointmentId ? { ...p, branch: editBranch.label, branch_id: editBranch.value } : p
          ),
        }))
      );
    }
    setShowBranchModal(false);
    setEditBranch(null);
    setEditAppointmentId(null);
    Swal.fire({ icon: "success", title: "Success!", text: `Branch updated to ${editBranch.label}`, timer: 1500, showConfirmButton: false });
  };

  // ==================== PAYMENT HANDLERS ====================
  const handleCollectPayment = (patient) => {
    setSelectedPatientForPayment({
      patient_id: patient.patient_id,
      lead_id: patient.lead_id,
      queue_id: patient.queue_id,
      name: patient.name,
      appointment_id: patient.appointment_id,
      date: patient.date,
      appointment_camp_reg: patient.appointment_camp_reg,
    });
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async (paymentData) => {
    setCollectingPayment(true);
    try {
      const finalPatientId = selectedPatientForPayment?.patient_id || "0";
      const finalLeadId = paymentData.lead_id || selectedPatientForPayment?.lead_id || "0";

      await collectAppointmentPayment({
        appointment_id: selectedPatientForPayment?.appointment_id,
        amount: paymentData.amount,
        payment_type: paymentData.paymentType,
        coupon_code: paymentData.couponCode || "",
        final_amount: paymentData.finalAmount,
        lead_id: finalLeadId,
        patient_id: finalPatientId,
        appointment_type: "APPOINTMENT",
      });

      Swal.fire("Success", "Payment collected successfully!", "success");
      setShowPaymentModal(false);
      await refreshBothLists();
    } catch (error) {
      console.error("Payment error:", error);
      Swal.fire("Error", "Payment failed. Please try again.", "error");
    } finally {
      setCollectingPayment(false);
    }
  };

  // ==================== RENDER ====================
  return (
    <div>
      <style>
        {`
          .accordion-button {
            border-radius: 0 !important;
          }
          .accordion-button:not(.collapsed) {
            border-radius: 0 !important;
          }
          .cursor-pointer {
            cursor: pointer;
          }
          .refreshing {
            opacity: 0.7;
            pointer-events: none;
          }
          .branch-edit-icon {
            cursor: pointer;
            color: #28a745;
            font-size: 14px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            margin-left: 6px;
            transition: all 0.2s ease;
          }
          .branch-edit-icon:hover {
            color: #1e7e34;
            transform: scale(1.1);
          }
          .action-icon {
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
          .action-icon .loader-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
          }
          .action-icon img, .action-icon span {
            cursor: pointer;
            transition: opacity 0.2s;
          }
          .action-icon.disabled {
            opacity: 0.5;
            pointer-events: none;
          }
          .reschedule-disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
          .reschedule-disabled img {
            cursor: not-allowed;
          }
        .lead-badge {
  background-color: #fff3cd;
  color: #856404;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 500;
  border: 1px solid #f1d98b;
}
          .lead-highlight {
            background-color: #fff3cd !important;
          }
        `}
      </style>

      {/* Filters Row */}
      <div className="row g-3 mb-3">
        <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12">
          <Select
            value={selectedDoctorOption || null}
            onChange={handleDoctorChange}
            options={doctorOptions}
            formatOptionLabel={formatOptionLabel}
            placeholder="Select a doctor..."
            isClearable
            className="mb-3"
            classNamePrefix="react-select"
            isDisabled={loading || isRefreshing.assigned || isRefreshing.confirmed}
            styles={customStyles}
          />
        </div>
        <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12 position-relative">
          <input
            type="date"
            className="form-control form-control-sm"
            value={selectedDate}
            min={getTodayDateString()}
            onChange={handleDateChange}
            disabled={isRefreshing.assigned || isRefreshing.confirmed}
          />
          <i
            className="fa fa-calendar position-absolute"
            style={{
              right: "30px",
              top: "35%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: "18px",
              zIndex: 1,
              color: "#555",
            }}
          />
        </div>
        <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12">
          <Select
            value={selectedBranch}
            onChange={handleBranchFilterChange}
            options={branches.map((branch) => ({
              value: branch.branch_id || branch.value,
              label: branch.branch_name || branch.label,
            }))}
            placeholder="Filter by Branch"
            classNamePrefix="select"
            styles={customStyles}
            isClearable
            isDisabled={isRefreshing.assigned || isRefreshing.confirmed}
          />
        </div>
      </div>

      {/* Main Content Row */}
      <div className="row">
        {/* LEFT: Booked (Unconfirmed) Appointments */}
        <div className="col-xl-6 col-xxl-6 col-lg-6">
          <div className="card border-0 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0" style={{ fontSize: "1.4rem", fontWeight: 600 }}>
                  Booked
                  {isRefreshing.assigned && (
                    <span className="ms-2">
                      <i className="fa fa-spinner fa-spin text-primary"></i>
                    </span>
                  )}
                </h4>
              </div>
              <div className="pt-3">
                <div
                  id="DZ_W_Todo5"
                  className={`widget-media dz-scroll ${isRefreshing.assigned ? "refreshing" : ""}`}
                  style={{ height: "600px", overflowY: "auto", overflowX: "hidden" }}
                >
                  {localAssignedAppointments.length > 0 ? (
                    <ul className="timeline p-0 m-0">
                      {localAssignedAppointments.map((patient) => {
                        const isLead = isLeadPatient(patient);
                        const showPaymentButton = shouldShowCollectPayment(patient);
                        // ✅ Validate selected value against current timeSlots
                        const currentSelectedSlot = selectedTimeSlots[patient.id] || null;
                        const validSelectedSlot = timeSlots.find(
                          (slot) => slot.value === currentSelectedSlot?.value
                        ) || null;

                        return (
                          <li key={patient.id} style={{ position: "relative" }}>
                            {/* Lead Badge - Top Right Corner */}
                          
                            <div
                              className={`timeline-panel d-flex align-items-start ${isLead ? "lead-highlight" : ""}`}
                              style={{
                                borderRadius: "8px",
                                padding: "10px",
                          
                                marginBottom: "10px",
                                position: "relative",
                              }}
                            >
                              <div className="me-3">
                                <Link to="#">
                                  <img
                                    src={patient.avatar || defaultAvatar}
                                    width="50"
                                    className="rounded-circle"
                                    alt={patient.name}
                                  />
                                </Link>
                              </div>
                              <div className="flex-grow-1">
                                <div className="row align-items-center g-2 mb-2 flex-wrap">
                                  <div className="col-md-6" style={{ minWidth: 0 }}>
                                  <h6
  className="mb-0 d-flex align-items-center flex-wrap"
  style={{
    fontSize: "18px",
    fontWeight: 600,
    gap: "8px",
  }}
>
  <span>
    {[patient.name, patient.disease]
      .filter(Boolean)
      .join(" | ")}
  </span>

  {/* LEAD BADGE */}
  {isLead && (
    <span className="lead-badge">
      Lead
    </span>
  )}
</h6>
                                    {(
  patient.mode ||
  patient.status ||
  formatDate(patient.date) ||
  patient.appointment_camp_reg
) && (
  <small className="text-muted d-block">
    {[
      patient.mode,
      patient.status,
      formatDate(patient.date),
      patient.appointment_camp_reg,
    ]
      .filter(Boolean)
      .join(" | ")}
  </small>
)}

<div className="d-flex align-items-center">
  <small className="text-muted">
    {patient.is_firsttime_visitor === "Yes"
      ? "New"
      : patient.is_firsttime_visitor === "No"
      ? "Old"
      : ""}
    {patient.is_firsttime_visitor ? " | " : ""}
    Branch: {patient.branch || "N/A"}
  </small>

  <span
    onClick={() => handleOpenBranchModal(patient, "assigned")}
    className="branch-edit-icon"
    title="Edit Appointment Branch"
  >
    <i className="fa fa-pencil"></i>
  </span>
</div>
                                   
                                  </div>
                                  <div className="col-md-4" style={{ minWidth: 0 }}>
                                    <Select
                                      key={`${selectedDoctorId}-${selectedDate}-${patient.id}`}
                                      options={timeSlots}
                                      value={validSelectedSlot}
                                      onChange={(selectedOption) => handleTimeSlotChange(selectedOption, patient.id)}
                                      placeholder="Time Slot"
                                      isClearable
                                      isLoading={slotLoading}
                                      styles={customStyles}
                                      menuPortalTarget={document.body}
                                      menuPosition="fixed"
                                      isDisabled={
                                        slotLoading ||
                                        isRefreshing.assigned ||
                                        confirmLoading[patient.appointment_id]
                                      }
                                    />
                                  </div>
                                  <div className="col-md-2 d-flex justify-content-end" style={{ minWidth: "100px" }}>
                                    <div className="d-flex gap-3 align-items-center flex-shrink-0">
                                      {/* Share Link */}
                                      <div className={`action-icon ${sendLinkLoading[patient.patient_id] ? "disabled" : ""}`}>
                                        <img
                                          src={icon}
                                          alt="Share Link"
                                          width="24"
                                          height="24"
                                          className="cursor-pointer"
                                          title="Share link"
                                          onClick={() => !sendLinkLoading[patient.patient_id] && handleSendLink(patient.patient_id)}
                                        />
                                        {sendLinkLoading[patient.patient_id] && (
                                          <div className="loader-overlay">
                                            <i className="fa fa-spinner fa-spin text-primary"></i>
                                          </div>
                                        )}
                                      </div>
                                      {/* Collect Payment - appears when payment is pending */}
                                      {showPaymentButton && (
                                        <div className="action-icon" onClick={() => handleCollectPayment(patient)}>
                                          <img
                                            src={collectPaymentIcon}
                                            alt="Collect Payment"
                                            width="24"
                                            height="24"
                                            className="cursor-pointer"
                                            title="Collect Payment"
                                          />
                                        </div>
                                      )}
                                      {/* Confirm Appointment */}
                                      <div className={`action-icon ${confirmLoading[patient.appointment_id] ? "disabled" : ""}`}>
                                        <img
                                          src={completeIcon}
                                          alt="Confirm"
                                          width="24"
                                          height="24"
                                          className="cursor-pointer"
                                          title="Confirm appointment"
                                          onClick={() => {
                                            if (confirmLoading[patient.appointment_id]) return;
                                            const selectedSlot = selectedTimeSlots[patient.id];
                                            if (!selectedSlot) {
                                              Swal.fire("Warning", "Please select a time slot first", "warning");
                                              return;
                                            }
                                            handleConfirmDoctorAssign(patient.appointment_id, patient.name, patient.branch_id, false);
                                          }}
                                        />
                                        {confirmLoading[patient.appointment_id] && (
                                          <div className="loader-overlay">
                                            <i className="fa fa-spinner fa-spin text-primary"></i>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <div className="text-center py-5">
                      <small className="text-muted">No booked appointments</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Confirmed Appointments by Time Slot */}
        <div className="col-xl-6 col-xxl-6 col-lg-6">
          <div className="card border-0 pb-0 h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                <div className="d-flex align-items-center">
                  <h4 className="mb-0" style={{ fontSize: "1.4rem", fontWeight: 600 }}>
                    Confirmed
                    {isRefreshing.confirmed && (
                      <span className="ms-2">
                        <i className="fa fa-spinner fa-spin text-primary"></i>
                      </span>
                    )}
                  </h4>
                  <span className="px-2 py-1 rounded-pill bg-info text-white ms-2" style={{ fontSize: "1rem", lineHeight: "normal" }}>
                    {summary?.total_confirmed || 0} / {summary?.allowedcount || 0}
                  </span>
                </div>
              </div>
              <div className="pt-3">
                <div
                  id="DZ_W_Todo6"
                  className={`widget-media dz-scroll ${isRefreshing.confirmed ? "refreshing" : ""}`}
                  style={{ height: "600px", overflowY: "auto" }}
                >
                  {localConfirmedAppointments.length > 0 ? (
                    <Accordion className="accordion accordion-no-gutter aacordion header-bg" defaultActiveKey="0">
                      {localConfirmedAppointments.map((slot, idx) => (
                        <Accordion.Item key={idx} eventKey={`${idx}`}>
                          <Accordion.Header
                            as="div"
                            className="bg-info text-white"
                            style={{ borderRadius: "0px", margin: 0, boxShadow: "none", border: "none" }}
                          >
                            <div className="d-flex justify-content-between align-items-center w-100 me-3">
                              <span style={{ fontWeight: "600", fontSize: "14px" }}>{slot.label}</span>
                              <span className="px-2 py-1 rounded-pill bg-white text-info" style={{ fontSize: "12px", fontWeight: "600" }}>
                                {slot.confirmed_count}/{slot.allowed_count}
                              </span>
                            </div>
                          </Accordion.Header>
                          <Accordion.Collapse eventKey={`${idx}`}>
                            <div className="accordion-body p-0">
                              <div className="row g-0">
                                {slot.patients.length > 0 ? (
                                  slot.patients.map((patient, pIdx) => {
                                    const isLead = isLeadPatient(patient);
                                    const showPaymentButton = shouldShowCollectPayment(patient);
                                    return (
                                      <div
                                        key={pIdx}
                                        className="col-12 border-bottom"
                                        style={{ backgroundColor: isLead ? "#fff3cd" : "transparent", position: "relative" }}
                                      >
                                        {/* Lead Badge - Top Right Corner */}
                                        {isLead && (
                                          <div style={{ position: "absolute", top: "8px", right: "8px", zIndex: 10 }}>
                                            <span className="lead-badge">Lead</span>
                                          </div>
                                        )}
                                        <div className="p-3">
                                          <div className="timeline-panel d-flex align-items-center">
                                            <div className="me-3">
                                              <Link to="#">
                                                <img
                                                  src={patient.avatar || defaultAvatar}
                                                  alt={patient.name || "Patient"}
                                                  width="45"
                                                  className="rounded-circle"
                                                />
                                              </Link>
                                            </div>
                                            <div className="media-body flex-grow-1">
                                              {patient.name && (
                                                <h6 className="mb-1" style={{ fontSize: "18px", fontWeight: 600 }}>
                                                  {patient.name}
                                                </h6>
                                              )}
                                              {[patient.mode, patient.appointment_status].some(Boolean) && (
                                                <small className="text-muted d-block">
                                                  {[patient.mode, patient.appointment_status].filter(Boolean).join(" | ")}
                                                </small>
                                              )}
                                              {[patient.phone_no, formatDate(patient.date), patient.timeslot].some(Boolean) && (
                                                <small className="text-muted d-block">
                                                  {[patient.phone_no, formatDate(patient.date), patient.timeslot].filter(Boolean).join(" | ")}
                                                </small>
                                              )}
                                              <div className="d-flex align-items-center">
                                                <small className="text-muted">Branch: {patient.branch || "N/A"}</small>
                                                <span
                                                  onClick={() => handleOpenBranchModal(patient, "confirmed")}
                                                  className="branch-edit-icon"
                                                  title="Edit Branch"
                                                >
                                                  <i className="fa fa-pencil"></i>
                                                </span>
                                              </div>
                                            </div>
                                            <div className="d-flex gap-2 align-items-center flex-shrink-0">
                                              {/* Reschedule Icon */}
                                              <div
                                                className={`action-icon ${rescheduleLoading ? "disabled" : ""} ${
                                                  patient.is_presented ? "reschedule-disabled" : ""
                                                }`}
                                              >
                                                <img
                                                  src={Reshedule}
                                                  alt="Reschedule"
                                                  width="24"
                                                  height="24"
                                                  className={!patient.is_presented ? "cursor-pointer" : ""}
                                                  onClick={() => {
                                                    if (rescheduleLoading || patient.is_presented) return;
                                                    setSelectedAppointment(patient);
                                                    setModalDate(patient.date || selectedDate);
                                                    setModalMode(patient.mode || "Online");
                                                    if (patient.branch_id) {
                                                      const branchOption = branches.find(
                                                        (b) => String(b.branch_id) === String(patient.branch_id)
                                                      );
                                                      setModalBranch(
                                                        branchOption
                                                          ? { value: branchOption.branch_id, label: branchOption.branch_name }
                                                          : null
                                                      );
                                                    } else {
                                                      setModalBranch(null);
                                                    }
                                                    setModalTimeSlot(
                                                      patient.timeslot ? { label: patient.timeslot, value: patient.timeslot } : null
                                                    );
                                                    setShowInfoModal(true);
                                                  }}
                                                  title={patient.is_presented ? "Patient is already in queue - Cannot reschedule" : "Reschedule Patient"}
                                                />
                                                {rescheduleLoading && selectedAppointment?.appointment_id === patient.appointment_id && (
                                                  <div className="loader-overlay">
                                                    <i className="fa fa-spinner fa-spin text-primary"></i>
                                                  </div>
                                                )}
                                              </div>
                                              {/* Share Link */}
                                              <div className={`action-icon ${sendLinkLoading[patient.patient_id] ? "disabled" : ""}`}>
                                                <img
                                                  src={icon}
                                                  alt="Share Link"
                                                  width="24"
                                                  height="24"
                                                  className="cursor-pointer me-1"
                                                  onClick={() => !sendLinkLoading[patient.patient_id] && handleSendLink(patient.patient_id)}
                                                  title="Share Link"
                                                />
                                                {sendLinkLoading[patient.patient_id] && (
                                                  <div className="loader-overlay">
                                                    <i className="fa fa-spinner fa-spin text-primary"></i>
                                                  </div>
                                                )}
                                              </div>
                                              {/* Collect Payment - appears when payment is pending */}
                                              {showPaymentButton && (
                                                <div className="action-icon" onClick={() => handleCollectPayment(patient)}>
                                                  <img
                                                    src={collectPaymentIcon}
                                                    alt="Collect Payment"
                                                    width="24"
                                                    height="24"
                                                    className="cursor-pointer"
                                                    title="Collect Payment"
                                                  />
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div className="col-12">
                                    <div className="p-3 text-center">
                                      <small className="text-muted">No appointments for this slot</small>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Accordion.Collapse>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  ) : (
                    <div className="text-center py-5">
                      <small className="text-muted">No confirmed appointments</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <CommonPaymentModal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        title="Collect Payment"
        patientName={selectedPatientForPayment?.name}
        initialAmount=""
        onSubmit={handlePaymentSubmit}
        loading={collectingPayment}
        paymentType="appointment"
        paymentData={{
          patient_id: selectedPatientForPayment?.patient_id,
          appointment_id: selectedPatientForPayment?.appointment_id,
          lead_id: selectedPatientForPayment?.lead_id,
          date: selectedPatientForPayment?.date,
          appointment_camp_reg: selectedPatientForPayment?.appointment_camp_reg,
        }}
        paymentFor="APPOINTMENT"
       
      />

      {/* Edit Branch Modal */}
      <Modal show={showBranchModal} onHide={() => setShowBranchModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Branch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">Please select a branch from the dropdown below to update the appointment branch.</p>
          <div className="mb-3">
            <label className="form-label fw-bold">Select Branch</label>
            <Select
              value={editBranch}
              onChange={setEditBranch}
              options={branches.map((branch) => ({
                value: branch.branch_id || branch.value,
                label: branch.branch_name || branch.label,
              }))}
              placeholder="Select Branch"
              isClearable
              styles={customStyles}
              isDisabled={branchUpdateLoading}
            />
          </div>
          {selectedAppointment && (
            <div className="alert alert-info mt-2 p-2">
              <small>
                <strong>Current Branch:</strong> {selectedAppointment.branch || "N/A"}
              </small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowBranchModal(false)}
            disabled={branchUpdateLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleBranchModalConfirm}
            disabled={branchUpdateLoading}
          >
            {branchUpdateLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Updating...
              </>
            ) : (
              "Confirm"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Reschedule Modal */}
      <Modal show={showInfoModal} onHide={() => setShowInfoModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reschedule Appointment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Date</label>
            <input
              type="date"
              className="form-control"
              value={modalDate}
              min={getTodayDateString()}
              onChange={(e) => setModalDate(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Branch</label>
            <Select
              value={modalBranch}
              onChange={setModalBranch}
              options={branches.map((branch) => ({
                value: branch.branch_id || branch.value,
                label: branch.branch_name || branch.label,
              }))}
              placeholder="Select Branch"
              styles={customStyles}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Mode</label>
            <select className="form-select" value={modalMode} onChange={(e) => setModalMode(e.target.value)}>
              <option value="Online">Online</option>
              <option value="Offline">Offline</option>
            </select>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowInfoModal(false)}
            disabled={rescheduleLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleRescheduleWithApi}
            disabled={rescheduleLoading}
          >
            {rescheduleLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Rescheduling...
              </>
            ) : (
              "Reschedule"
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssignedAppointment;